import React, { useState } from 'react';
import { FormProvider, UseFormReturn, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Section } from '@/components/atoms/Section';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/form/Input';
import { ImageInput } from '@/components/form/ImageInput';
import BaseMultiSelect from '@/components/atoms/MultiSelect';
import { Label } from '@/components/atoms/Label';
import { computeText } from '@/locales/utils';
import type { RestaurantPayload } from '@/services/innovorder/restaurant/restaurant.type';
import type { BrandRestaurantFamily } from '@/services/innovorder/brand/brand.type';
import type { RestaurantFormValues } from '../RestaurantInfo.viewmodel';
import { FormGridThreeCols, SectionActions, ChangesInfo, ErrorText } from '../RestaurantInfo.style';
import { AddressAutocomplete } from './AddressAutocomplete';
import { MapPreview } from './MapPreview';

type RestaurantInfoSectionProps = {
    form: UseFormReturn<RestaurantFormValues>;
    restaurant?: RestaurantPayload;
    brandFamilies: BrandRestaurantFamily[];
    hasRightOnRestaurantInfo: boolean;
    brandId: number;
    changesCount: number;
    isSaving: boolean;
    onSave: () => Promise<void>;
    onCancel: () => void;
    addressText: string;
    onAddressChange: (address: { name: string; lat: number; lng: number; providerId: string }) => void;
    addressCoords?: { lat: number; lng: number } | null;
};

export const RestaurantInfoSection: React.FunctionComponent<RestaurantInfoSectionProps> = ({
    form,
    restaurant,
    brandFamilies,
    hasRightOnRestaurantInfo,
    brandId,
    changesCount,
    isSaving,
    onSave,
    onCancel,
    addressText,
    onAddressChange,
    addressCoords,
}) => {
    const intl = useIntl();
    const [saveError, setSaveError] = useState<string>();

    const handleSave = async () => {
        try {
            setSaveError(undefined);
            await onSave();
        } catch {
            setSaveError(computeText(intl, 'restaurantInfo.save.error'));
        }
    };

    const familyOptions = brandFamilies.map((f) => ({
        label: f.name,
        value: f.brandRestaurantFamilyId,
    }));

    const addr = restaurant?.address as { lat?: number; lng?: number } | undefined;
    const mapLat = addressCoords?.lat ?? addr?.lat ?? 0;
    const mapLng = addressCoords?.lng ?? addr?.lng ?? 0;

    return (
        <Section title="restaurantInfo.information.title">
            <FormProvider {...form}>
                <FormGridThreeCols>
                    <Input
                        name="name"
                        labelId="restaurantInfo.name"
                        rules={{ required: computeText(intl, 'field.required') }}
                    />
                    <AddressAutocomplete value={addressText} onChange={onAddressChange} />
                    <Input
                        name="contactName"
                        labelId="restaurantInfo.contactName"
                        rules={{ required: computeText(intl, 'field.required') }}
                    />
                    <Input
                        name="contactEmail"
                        labelId="restaurantInfo.contactEmail"
                        rules={{
                            required: computeText(intl, 'field.required'),
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: computeText(intl, 'field.invalidEmail'),
                            },
                        }}
                    />
                    <Input
                        name="contactPhone"
                        labelId="restaurantInfo.contactPhone"
                        rules={{ required: computeText(intl, 'field.required') }}
                    />
                    <Input name="externalId" labelId="restaurantInfo.externalId" />
                    {hasRightOnRestaurantInfo && (
                        <>
                            <Input name="restaurantTag" labelId="restaurantInfo.tag" />
                            <Controller
                                control={form.control}
                                name="brandRestaurantFamilies"
                                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                    <div>
                                        <Label labelId="restaurantInfo.families" inputId="restaurantInfo.families" />
                                        <BaseMultiSelect<number>
                                            inputId="restaurantInfo.families"
                                            isMulti
                                            isDisabled={familyOptions.length === 0}
                                            options={familyOptions}
                                            onChange={(options) => {
                                                onChange(options.map((option) => option.value));
                                            }}
                                            onBlur={onBlur}
                                            placeholder={computeText(intl, 'restaurantInfo.searchFamilies')}
                                            value={(value || []).map((id: number) => {
                                                const family = familyOptions.find((f) => f.value === id);
                                                return family || { label: String(id), value: id };
                                            })}
                                            error={error}
                                        />
                                    </div>
                                )}
                            />
                            <div />
                            <ImageInput name="imageUrl" brandId={brandId} />
                            <MapPreview lat={mapLat} lng={mapLng} />
                        </>
                    )}
                </FormGridThreeCols>
            </FormProvider>
            {changesCount > 0 && (
                <SectionActions>
                    <ChangesInfo>
                        <FormattedMessage id="savingBar.changesDone" values={{ changesCount }} />
                    </ChangesInfo>
                    <Button buttonType="secondary" onClick={onCancel} isLoading={isSaving}>
                        <FormattedMessage id="button.cancel" />
                    </Button>
                    <Button buttonType="primary" onClick={handleSave} isLoading={isSaving}>
                        <FormattedMessage id="button.save" />
                    </Button>
                </SectionActions>
            )}
            {saveError && <ErrorText>{saveError}</ErrorText>}
        </Section>
    );
};
